import { useCallback, useState } from 'react'
import type { AsyncState } from './Async.js'
import { useLoaded } from './useLoaded.js'

/**
 * A paged listing that loads itself.
 *
 * `useLoaded` with a page number in front of it. Changing the page rebuilds the
 * loader, which reloads — and because `useLoaded` clears the value first, the
 * previous page never sits on screen pretending to be the new one.
 */
export function usePaged<T>(
  load: (page: number) => Promise<T>,
  detail: string,
): {
  readonly page: number
  readonly state: AsyncState<T>
  readonly setPage: (page: number) => void
  readonly retry: () => Promise<void>
} {
  const [page, setPage] = useState(1)
  const forPage = useCallback(() => load(page), [load, page])
  const { state, reload } = useLoaded(forPage, detail)

  return { page, state, setPage, retry: reload }
}
